import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { Typography, Flex, Spin, Button } from 'antd';
import { getRedirectRoute } from '@/utils/redirect';
import { getPendingTenantSelectionFlag, clearPendingTenantSelectionFlag } from '@/utils/loginFlow';
import AuthPageShell from '@/components/auth/AuthPageShell';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { verifyEmailMutationFn } from '@/api/auth/mutations';
import { getApiErrorMessage } from '@/utils/apiError';
import { useAuth } from '@/hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import { authKeys } from '@/api/auth/keys';

const { Text } = Typography;

type VerifyEmailSearch = {
  token?: string;
};

export const Route = createFileRoute('/verify-email')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): VerifyEmailSearch => ({
    token: (search.token as string) || undefined
  }),
  beforeLoad: ({ context, search }) => {
    if (search.token) return;
    if (!context?.auth) clearPendingTenantSelectionFlag();
    if (context?.auth && !getPendingTenantSelectionFlag()) {
      throw redirect({ to: getRedirectRoute(context.auth) });
    }
  }
});

function RouteComponent() {
  const { token } = Route.useSearch();
  const { t } = useTranslation(['auth', 'common']);
  const { payload } = useAuth();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'idle' | 'verifying' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage(t('auth:verifyEmail.invalidToken'));
      return;
    }

    const run = async () => {
      setStatus('verifying');
      try {
        await verifyEmailMutationFn({ token });
        await queryClient.invalidateQueries({ queryKey: authKeys.profile() });
        setStatus('success');
      } catch (error: unknown) {
        setStatus('error');
        setErrorMessage(getApiErrorMessage(error, t('auth:verifyEmail.failed')));
      }
    };

    run();
  }, [token, t, queryClient]);

  const renderContent = () => {
    if (status === 'verifying' || status === 'idle') {
      return (
        <Flex vertical align="center" gap={16}>
          <Spin size="large" />
          <Text>{t('auth:verifyEmail.verifying')}</Text>
        </Flex>
      );
    }

    if (status === 'success') {
      return (
        <Flex vertical align="center" gap={16}>
          <Text>{payload ? t('auth:verifyEmail.successSignedIn') : t('auth:verifyEmail.success')}</Text>
          {payload ? (
            <Link to="/profile">
              <Button type="primary">{t('auth:verifyEmail.backToProfile')}</Button>
            </Link>
          ) : (
            <Link to="/login">
              <Button type="primary">{t('auth:verifyEmail.backToLogin')}</Button>
            </Link>
          )}
        </Flex>
      );
    }

    return (
      <Flex vertical align="center" gap={16}>
        <Text type="danger">{errorMessage}</Text>
        <Link to="/login">{t('auth:verifyEmail.backToLogin')}</Link>
      </Flex>
    );
  };

  return (
    <AuthPageShell title={t('auth:verifyEmail.title')}>
      {renderContent()}
    </AuthPageShell>
  );
}
